// Generated by GitHub Copilot
/**
 * Cross-tab navigation + JS-error history stored in a capped ring buffer in a
 * single `localStorage` key shared across tabs, and shipped to OpenObserve through
 * `telemetry-sender.ts`. Last-writer-wins under concurrency is intentional
 * (best-effort). Never stores secrets/tokens; stacks and messages are truncated.
 */
import type { AnyRouter } from '@tanstack/react-router'
import { enqueueTelemetryEvent, type TelemetryEvent } from '#core/diagnostics/telemetry-sender.ts'

const STORAGE_KEY = 'ynteq.diag.events'
const TAB_ID_KEY = 'ynteq.diag.tabId'
const MAX_EVENTS = 50
const MAX_BYTES = 32 * 1024
const MAX_STACK = 2000
const MAX_MESSAGE = 1000
const MAX_URL = 2048
const MAX_ROUTE_ID = 256
const MAX_NAV_SOURCE = 32
const NAV_SOURCE_TTL_MS = 2000

export type DiagnosticEventType = 'navigate' | 'error' | 'unhandledrejection'

/** One captured navigation or error event. */
export interface DiagnosticEvent {
  /** Epoch milliseconds when the event was recorded. */
  ts: number
  /** Short per-tab id (sessionStorage) so events can be attributed to a tab. */
  tabId: string
  type: DiagnosticEventType
  url: string
  /** TanStack route id of the resolved leaf match, e.g. `/_app/$companyId/work-orders`. */
  routeId?: string
  /** Where the navigation was triggered from (`side_menu`, `bottom_nav`), when known. */
  navSource?: string
  message?: string
  stack?: string
}

function truncate(value: string | undefined, max: number): string | undefined {
  if (value == null) return undefined
  return value.length > max ? value.slice(0, max) : value
}

let pendingNavSource: string | undefined
let pendingNavSourceTimer: ReturnType<typeof setTimeout> | undefined

/**
 * Labels the NEXT navigation with where it was triggered from. The mark expires on its
 * own, so a click that resolves no navigation (already on that page) cannot mislabel a
 * later one.
 */
export function markNavigationSource(source: 'side_menu' | 'bottom_nav'): void {
  pendingNavSource = source
  clearTimeout(pendingNavSourceTimer)
  pendingNavSourceTimer = setTimeout(() => {
    pendingNavSource = undefined
  }, NAV_SOURCE_TTL_MS)
}

function consumeNavSource(): string | undefined {
  const source = pendingNavSource
  pendingNavSource = undefined
  clearTimeout(pendingNavSourceTimer)
  return source
}

let cachedTabId: string | null = null

/** Returns this tab's short id, generating + persisting one in sessionStorage on first use. */
export function getTabId(): string {
  if (cachedTabId) return cachedTabId
  try {
    let id = sessionStorage.getItem(TAB_ID_KEY)
    if (!id) {
      id = Math.random().toString(36).slice(2, 10)
      sessionStorage.setItem(TAB_ID_KEY, id)
    }
    cachedTabId = id
    return id
  } catch {
    cachedTabId = 'unknown'
    return cachedTabId
  }
}

/** Reads the shared event buffer (oldest first). Returns `[]` on any parse/storage failure. */
export function readEvents(): DiagnosticEvent[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? (parsed as DiagnosticEvent[]) : []
  } catch {
    return []
  }
}

/** Clears the shared buffer (e.g. after a successful report submit). */
export function clearEvents(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore — best-effort
  }
}

function writeEvents(events: DiagnosticEvent[]): void {
  // Trim to the count cap, then drop oldest until under the byte cap.
  let trimmed = events.slice(-MAX_EVENTS)
  try {
    let serialized = JSON.stringify(trimmed)
    while (serialized.length > MAX_BYTES && trimmed.length > 1) {
      trimmed = trimmed.slice(1)
      serialized = JSON.stringify(trimmed)
    }
    localStorage.setItem(STORAGE_KEY, serialized)
  } catch {
    // ignore (quota / disabled storage) — diagnostics are best-effort
  }
}

function toTelemetryEvent(entry: DiagnosticEvent): TelemetryEvent {
  return {
    kind: entry.type === 'navigate' ? 'navigation' : entry.type,
    ts: new Date(entry.ts).toISOString(),
    url: truncate(entry.url, MAX_URL) ?? '',
    route_id: entry.routeId ?? null,
    nav_source: entry.navSource ?? null,
    message: entry.message ?? null,
    stack: entry.stack ?? null,
  }
}

/** Appends one event to the shared buffer (last-writer-wins, best-effort) and ships it. */
export function recordEvent(event: Omit<DiagnosticEvent, 'ts' | 'tabId'>): void {
  const entry: DiagnosticEvent = {
    ts: Date.now(),
    tabId: getTabId(),
    type: event.type,
    url: event.url,
    routeId: truncate(event.routeId, MAX_ROUTE_ID),
    navSource: truncate(event.navSource, MAX_NAV_SOURCE),
    message: truncate(event.message, MAX_MESSAGE),
    stack: truncate(event.stack, MAX_STACK),
  }
  writeEvents([...readEvents(), entry])
  enqueueTelemetryEvent(toTelemetryEvent(entry), entry.tabId)
}

let initialized = false

/**
 * Wires the early window error/rejection listeners and a router navigation
 * collector. Idempotent — safe to call once at app startup.
 *
 * @param router - The app router; navigations are captured on `onResolved`.
 */
export function initDiagnostics(router: AnyRouter): void {
  if (initialized) return
  initialized = true

  // Seed the tab id so the first events are attributable.
  getTabId()

  router.subscribe('onResolved', () => {
    recordEvent({
      type: 'navigate',
      url: location.href,
      routeId: router.state.matches.at(-1)?.routeId,
      navSource: consumeNavSource(),
    })
  })

  window.addEventListener('error', (e: ErrorEvent) => {
    recordEvent({
      type: 'error',
      url: location.href,
      message: e.message,
      stack: e.error instanceof Error ? e.error.stack : undefined,
    })
  })

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason: unknown = e.reason
    recordEvent({
      type: 'unhandledrejection',
      url: location.href,
      message: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? reason.stack : undefined,
    })
  })
}
